import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Archive,
  Bot,
  ChevronDown,
  ChevronRight,
  MoreHorizontal,
  Pencil,
  Plus,
  WandSparkles,
  Inbox,
  Mail,
  MessageCircle,
  MessageCircleWarning,
  PanelLeftClose,
  PanelLeftOpen,
  Pin,
  Search,
  Settings2,
  Shuffle,
  Tag,
  User,
  Users,
  UsersRound,
  type LucideIcon,
} from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/misc'
import { statusMeta, useInboxAccounts, type ChannelAccount } from '@/hooks/use-integrations'
import { otherAccountUnread, othersUnreadTotal, shortUnreadCount } from '@/lib/inbox-account'
import { useMyPermissions, useTags } from '@/hooks/use-settings'
import type { ConversationCounts } from '@/hooks/use-conversations'
import { ReconnectButton } from '@/components/shared/reconnect-account'
import { isAccountDown } from '@/lib/account-status'
import { Input } from '@/components/ui/input'
import { PlatformBadge, PlatformTag } from '@/components/shared/platform-mark'
import { ChannelChips, type ChannelFilterValue } from '@/components/shared/channel-chips'
import { useChannelPicker } from '@/hooks/use-channel-picker'
import { cn, initials } from '@/lib/utils'
import { i18n } from '@/lib/i18n'
import { FilterSettingsDialog } from './filter-settings-dialog'
import { TagEditorDialog, TagRulesDialog } from './tag-dialogs'
import { AccountRail } from './account-rail'
import type { TagDef } from '@/hooks/use-settings'
import type { ChannelGroup } from '@/lib/channel-groups'

// ─────────────────────────────────────────────────────────────────────────────
// Kiểu dữ liệu
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Bộ lọc của Hộp thư. `archived` = kho lưu trữ (tài khoản đã gỡ). `mine`,
 * `unassigned`, `ai`, `direct`, `group` là chế độ xem phía backend (`?view=`).
 */
export type ConvFilter =
  | 'all'
  | 'unread'
  | 'unreplied'
  | 'mine'
  | 'unassigned'
  | 'ai'
  | 'pinned'
  | 'direct'
  | 'group'
  | 'archived'

/** Mục cấu hình được trong cột bộ lọc — gồm các bộ lọc + nhóm "Nhãn". */
export type FilterSlot = ConvFilter | 'tags'

export interface FilterRailProps {
  filter: ConvFilter
  onFilterChange: (f: ConvFilter) => void
  /** Nhãn khách đang lọc (bỏ trống = không lọc nhãn). */
  tag?: string
  onTagChange: (tag?: string) => void
  accountId?: string
  onAccountChange: (id?: string) => void
  /** Hàng chọn tài khoản (bản mở rộng, máy tính): kênh trước, tài khoản của kênh đó sau — xem account-rail.tsx. */
  channelGroups?: ChannelGroup[]
  channel?: ChannelFilterValue
  onChannelChange?: (v: ChannelFilterValue) => void
  /** Để đồng bộ thứ tự tài khoản đã xếp giữa điện thoại và máy tính (hooks/use-mobile-channels.ts). */
  userId?: string | null
  counts?: ConversationCounts
  /** Đang mở một chế độ xem tuỳ chỉnh → không tô bộ lọc có sẵn nào. */
  viewActive?: boolean
  /** Mục "Chế độ xem tuỳ chỉnh" (saved-views.tsx) — vẽ cuối cột ở cả hai dạng. `afterSelect`: ngăn kéo điện thoại dùng để tự đóng. */
  renderCustomViews?: (expanded: boolean, afterSelect?: () => void) => ReactNode
  /**
   * 'rail' (mặc định, máy tính) = cột icon ⇄ mở rộng. 'drawer' (điện thoại) = luôn mở rộng, chiếm hết bề rộng ngăn kéo
   * trượt từ trái: hàng lớn hơn, nút thao tác nhãn luôn hiện (điện thoại không có rê chuột), không có nút thu gọn / chọn tài khoản
   * (hàng avatar kênh ở đầu trang Hộp thư đã làm việc đó).
   */
  variant?: 'rail' | 'drawer'
  /** Gọi sau khi chọn bộ lọc / nhãn / chế độ xem — ngăn kéo dùng để đóng. */
  onNavigate?: () => void
  /** Khối chèn dưới tiêu đề (ngăn kéo: Ưu tiên/Nội bộ + nhóm kênh). */
  topSlot?: ReactNode
  /** Nút thêm ở hàng tiêu đề của ngăn kéo (bộ lọc điều kiện nâng cao — phễu). */
  headerSlot?: ReactNode
}

interface FilterDef {
  label: string
  icon: LucideIcon
  hint?: string
  count?: (c: ConversationCounts) => number | undefined
  /** Số cần chú ý → badge đỏ ở bản thu gọn. */
  alert?: boolean
}

export const FILTER_DEFS: Record<FilterSlot, FilterDef> = {
  all: { get label() { return i18n.t('convList:rail.f.all.label') }, icon: Inbox, count: (c) => c.total },
  unread: { get label() { return i18n.t('convList:rail.f.unread.label') }, icon: Mail, count: (c) => c.unread, alert: true },
  unreplied: { get label() { return i18n.t('convList:rail.f.unreplied.label') }, icon: MessageCircleWarning, count: (c) => c.unreplied, alert: true },
  mine: { get label() { return i18n.t('convList:rail.f.mine.label') }, icon: User, get hint() { return i18n.t('convList:rail.f.mine.hint') }, count: (c) => c.views?.mine },
  unassigned: { get label() { return i18n.t('convList:rail.f.unassigned.label') }, icon: Shuffle, get hint() { return i18n.t('convList:rail.f.unassigned.hint') }, count: (c) => c.views?.unassigned },
  ai: { get label() { return i18n.t('convList:rail.f.ai.label') }, icon: Bot, get hint() { return i18n.t('convList:rail.f.ai.hint') }, count: (c) => c.views?.ai },
  pinned: { get label() { return i18n.t('convList:rail.f.pinned.label') }, icon: Pin, count: (c) => c.views?.pinned },
  direct: { get label() { return i18n.t('convList:rail.f.direct.label') }, icon: MessageCircle, count: (c) => c.views?.direct },
  group: { get label() { return i18n.t('convList:rail.f.group.label') }, icon: UsersRound, count: (c) => c.views?.group },
  tags: { get label() { return i18n.t('convList:rail.f.tags.label') }, icon: Tag, get hint() { return i18n.t('convList:rail.f.tags.hint') } },
  archived: { get label() { return i18n.t('convList:rail.f.archived.label') }, icon: Archive, get hint() { return i18n.t('convList:rail.f.archived.hint') } },
}

/** Tên bộ lọc — cũng là tiêu đề cột danh sách hội thoại. Getter → luôn theo ngôn ngữ đang chọn. */
export const FILTER_LABEL = Object.defineProperties(
  {} as Record<FilterSlot, string>,
  Object.fromEntries(Object.keys(FILTER_DEFS).map((k) => [k, { enumerable: true, get: () => FILTER_DEFS[k as FilterSlot].label }])),
)

/** Thứ tự mặc định. "Tất cả" luôn ở đầu và không ẩn được (như "Trò chuyện" của Lark). */
export const DEFAULT_SLOTS: FilterSlot[] = [
  'all', 'unread', 'unreplied', 'mine', 'unassigned', 'ai', 'pinned', 'direct', 'group', 'tags', 'archived',
]

const SLOTS_KEY = 'ecdp_inbox_filters'
const EXPANDED_KEY = 'ecdp_inbox_filters_expanded'

function loadSlots(): FilterSlot[] {
  try {
    const raw = JSON.parse(localStorage.getItem(SLOTS_KEY) ?? 'null') as unknown
    if (!Array.isArray(raw)) return DEFAULT_SLOTS
    const valid = raw.filter((x): x is FilterSlot => typeof x === 'string' && x in FILTER_DEFS)
    return ['all', ...valid.filter((x) => x !== 'all')]
  } catch {
    return DEFAULT_SLOTS
  }
}

/** Rút gọn số lớn để badge không bị tràn (99+). */
function shortCount(n: number): string {
  return n > 99 ? '99+' : String(n)
}

// ─────────────────────────────────────────────────────────────────────────────
// Cột bộ lọc: thu gọn (icon) ⇄ mở rộng (tên + số đếm, kiểu Lark)
// ─────────────────────────────────────────────────────────────────────────────

export function FilterRail({
  filter,
  onFilterChange,
  tag,
  onTagChange,
  accountId,
  onAccountChange,
  channelGroups = [],
  channel = 'all',
  onChannelChange,
  userId,
  counts,
  viewActive,
  renderCustomViews,
  variant = 'rail',
  onNavigate,
  topSlot,
  headerSlot,
}: FilterRailProps) {
  const { t } = useTranslation('convList')
  const { data: railAccounts } = useInboxAccounts()
  const drawer = variant === 'drawer'
  const [expanded, setExpanded] = useState(() => drawer || localStorage.getItem(EXPANDED_KEY) === '1')
  const [slots, setSlots] = useState<FilterSlot[]>(loadSlots)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [tagsOpen, setTagsOpen] = useState(true)
  const { data: tags = [] } = useTags()
  // Quyền hiệu lực (BE vẫn chốt lại) — không có quyền thì ẩn hẳn nút (BRD §5.3.1).
  const { data: perms } = useMyPermissions()
  // Quy tắc nhãn là luồng Tự động hoá → BE đòi automation.create (tạo) / automation.update (sửa).
  const canRule = !!perms?.has('automation.create') || !!perms?.has('automation.update')
  // Thêm / sửa nhãn → tags.create / tags.update (Quản lý / Nhân viên mặc định chỉ có tags.view).
  const canAddTag = !!perms?.has('tags.create')
  const canEditTag = !!perms?.has('tags.update')
  const [tagEditor, setTagEditor] = useState<{ open: boolean; tag: TagDef | null }>({ open: false, tag: null })
  const [tagRules, setTagRules] = useState<{ open: boolean; tag: TagDef | null }>({ open: false, tag: null })

  const toggleExpanded = () => {
    const next = !expanded
    localStorage.setItem(EXPANDED_KEY, next ? '1' : '0')
    setExpanded(next)
  }
  const saveSlots = (next: FilterSlot[]) => {
    localStorage.setItem(SLOTS_KEY, JSON.stringify(next))
    setSlots(next)
  }
  const pick = (f: ConvFilter) => {
    onFilterChange(f)
    if (tag) onTagChange(undefined)
    onNavigate?.()
  }
  const pickTag = (name: string) => {
    onFilterChange('all')
    onTagChange(name)
    onNavigate?.()
  }
  const ROWC = drawer ? ROW_DRAWER : ROW
  const isActive = (f: ConvFilter) => filter === f && !tag && !viewActive
  const countOf = (slot: FilterSlot) => (counts ? FILTER_DEFS[slot].count?.(counts) : undefined)

  const settings = (
    <>
      <FilterSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} value={slots} onSave={saveSlots} />
      <TagEditorDialog
        open={tagEditor.open}
        tag={tagEditor.tag}
        onOpenChange={(open) => setTagEditor((s) => ({ ...s, open }))}
        // Tạo nhãn mới xong → mời đặt quy tắc tự gắn luôn (như Lark).
        onSaved={(saved) => !tagEditor.tag && perms?.has('automation.create') && setTagRules({ open: true, tag: saved })}
      />
      <TagRulesDialog
        open={tagRules.open}
        tag={tagRules.tag}
        onOpenChange={(open) => setTagRules((s) => ({ ...s, open }))}
      />
    </>
  )

  // ── Bản thu gọn: cột icon ─────────────────────────────────────────────────
  if (!expanded) {
    return (
      <aside className="flex w-14 shrink-0 flex-col items-center gap-1 bg-subnav py-3">
        <IconButton label={t('rail.expand')} onClick={toggleExpanded} icon={PanelLeftOpen} />
        <AccountPicker accountId={accountId} onAccountChange={onAccountChange} expanded={false} unreadByAccount={counts?.unreadByAccount} />
        {/* w-full: khung cuộn rộng bằng cả cột (chứ không chỉ bằng nút 40px) — huy hiệu đỏ nhô ra ngoài nút ("99+") không bị `overflow-y-auto` cắt cạnh phải. */}
        <nav className="flex w-full flex-1 flex-col items-center gap-1 overflow-y-auto pt-1">
          {slots.map((slot) => {
            if (slot === 'tags') {
              return (
                <DropdownMenu key="tags">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DropdownMenuTrigger asChild>
                        <button type="button" aria-label={t('rail.f.tags.label')} className={cn(ICON_BTN, tag ? ICON_ACTIVE : ICON_IDLE)}>
                          <Tag className="h-5 w-5" />
                        </button>
                      </DropdownMenuTrigger>
                    </TooltipTrigger>
                    <TooltipContent side="right">{tag ? t('rail.tagLabel', { tag }) : t('rail.f.tags.label')}</TooltipContent>
                  </Tooltip>
                  <DropdownMenuContent side="right" align="start" className="max-h-72 w-56 overflow-y-auto">
                    <DropdownMenuLabel>{t('rail.filterByTag')}</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {tags.length === 0 && <EmptyTags canAdd={canAddTag} />}
                    {tags.map((tg) => (
                      <DropdownMenuItem key={tg.id} onClick={() => pickTag(tg.name)}>
                        <span className="mr-2 h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: tg.color }} />
                        <span className="truncate">{tg.name}</span>
                      </DropdownMenuItem>
                    ))}
                    {(canAddTag || canRule) && <DropdownMenuSeparator />}
                    {canAddTag && (
                      <DropdownMenuItem onClick={() => setTagEditor({ open: true, tag: null })}>
                        <Plus className="mr-2 h-4 w-4" /> {t('rail.addTag')}
                      </DropdownMenuItem>
                    )}
                    {canRule && (
                      <DropdownMenuItem onClick={() => setTagRules({ open: true, tag: null })}>
                        <WandSparkles className="mr-2 h-4 w-4" /> {t('rail.tagRules')}
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )
            }
            const def = FILTER_DEFS[slot]
            const n = countOf(slot) ?? 0
            return (
              <Tooltip key={slot}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label={def.label}
                    aria-pressed={isActive(slot)}
                    onClick={() => pick(slot)}
                    className={cn(ICON_BTN, isActive(slot) ? ICON_ACTIVE : ICON_IDLE)}
                  >
                    <def.icon className="h-5 w-5" />
                    {def.alert && n > 0 && (
                      <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-none text-destructive-foreground">
                        {shortCount(n)}
                      </span>
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">{def.hint ?? def.label}</TooltipContent>
              </Tooltip>
            )
          })}
          {renderCustomViews?.(false)}
        </nav>
        <IconButton label={t('rail.customize')} onClick={() => setSettingsOpen(true)} icon={Settings2} />
        {settings}
      </aside>
    )
  }

  // ── Bản mở rộng: tên + số đếm ────────────────────────────────────────────
  return (
    <aside className={cn('flex shrink-0 flex-col', drawer ? 'h-full min-h-0 w-full bg-background' : 'w-56 bg-subnav')}>
      <div className={cn('flex shrink-0 items-center gap-1 px-3', drawer ? 'h-16' : 'h-14')}>
        <h2 className={cn('flex-1 truncate font-medium', drawer ? 'px-1 text-2xl font-semibold' : 'text-base')}>{drawer ? t('mhdr.drawerTitle') : t('rail.inbox')}</h2>
        {headerSlot}
        <IconButton label={t('rail.customize')} onClick={() => setSettingsOpen(true)} icon={Settings2} small={!drawer} />
        {!drawer && <IconButton label={t('rail.collapse')} onClick={toggleExpanded} icon={PanelLeftClose} small />}
      </div>
      {topSlot}
      {!drawer && onChannelChange && (
        <div className="px-2 pb-2">
          <AccountRail
            accounts={railAccounts ?? []}
            accountId={accountId}
            onAccountChange={onAccountChange}
            unreadByAccount={counts?.unreadByAccount}
            channelGroups={channelGroups}
            channel={channel}
            onChannelChange={onChannelChange}
            userId={userId}
          />
        </div>
      )}
      <nav className="min-h-0 flex-1 space-y-0.5 overflow-y-auto px-2 pb-3">
        {slots.map((slot) => {
          const def = FILTER_DEFS[slot]
          if (slot === 'tags') {
            return (
              <div key="tags">
                <div className="group/tags relative">
                  <button
                    type="button"
                    onClick={() => setTagsOpen((o) => !o)}
                    className={cn(ROWC, ROW_IDLE)}
                    aria-expanded={tagsOpen}
                  >
                    {tagsOpen ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
                    <span className="flex-1 truncate text-left">{def.label}</span>
                  </button>
                  {/* Thao tác nhãn — hiện khi rê chuột (luôn hiện với bàn phím) */}
                  <div className={cn('absolute right-1 top-1/2 flex -translate-y-1/2 items-center gap-0.5 transition-opacity', drawer ? 'opacity-100' : 'opacity-0 focus-within:opacity-100 group-hover/tags:opacity-100')}>
                    {canRule && (
                      <RowAction label={t('rail.tagRules')} icon={WandSparkles} onClick={() => setTagRules({ open: true, tag: null })} />
                    )}
                    {canAddTag && (
                      <RowAction label={t('rail.addTag')} icon={Plus} onClick={() => setTagEditor({ open: true, tag: null })} />
                    )}
                  </div>
                </div>
                {tagsOpen && (
                  <div className="space-y-0.5 pl-3">
                    {tags.length === 0 && <EmptyTags drawer={drawer} canAdd={canAddTag} />}
                    {tags.map((tg) => (
                      <div key={tg.id} className="group/tag relative">
                        <button
                          type="button"
                          onClick={() => pickTag(tg.name)}
                          className={cn(ROWC, 'pr-12', tag === tg.name ? ROW_ACTIVE : ROW_IDLE)}
                        >
                          <Tag className={drawer ? 'h-5 w-5 shrink-0' : 'h-4 w-4 shrink-0'} style={{ color: tg.color }} />
                          <span className="flex-1 truncate text-left">{tg.name}</span>
                        </button>
                        {(canEditTag || canRule) && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                type="button"
                                aria-label={t('rail.tagActions', { name: tg.name })}
                                className={cn(
                                  'absolute right-1 top-1/2 flex -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-opacity hover:bg-card hover:text-foreground',
                                  drawer ? 'h-9 w-9' : 'h-7 w-7 opacity-0 focus-visible:opacity-100 group-hover/tag:opacity-100 data-[state=open]:opacity-100',
                                )}
                              >
                                <MoreHorizontal className="h-4 w-4" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent side="right" align="start" className="w-52">
                              {canEditTag && (
                                <DropdownMenuItem onClick={() => setTagEditor({ open: true, tag: tg })}>
                                  <Pencil className="mr-2 h-4 w-4" /> {t('rail.editTag')}
                                </DropdownMenuItem>
                              )}
                              {canRule && (
                                <DropdownMenuItem onClick={() => setTagRules({ open: true, tag: tg })}>
                                  <WandSparkles className="mr-2 h-4 w-4" /> {t('rail.tagRules')}
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )
          }
          const n = countOf(slot)
          return (
            <button
              key={slot}
              type="button"
              title={def.hint}
              aria-pressed={isActive(slot)}
              onClick={() => pick(slot)}
              className={cn(ROWC, isActive(slot) ? ROW_ACTIVE : ROW_IDLE)}
            >
              <def.icon className={drawer ? 'h-5 w-5 shrink-0' : 'h-4 w-4 shrink-0'} />
              <span className="flex-1 truncate text-left">{def.label}</span>
              {n ? (
                <span className={cn('tabular-nums', def.alert ? 'font-medium text-destructive' : 'text-muted-foreground')}>
                  {shortCount(n)}
                </span>
              ) : null}
            </button>
          )
        })}
        {renderCustomViews?.(true, onNavigate)}
      </nav>
      {settings}
    </aside>
  )
}

const ICON_BTN =
  'relative flex h-10 w-10 shrink-0 items-center justify-center rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
const ICON_ACTIVE = 'bg-card text-primary shadow-sm'
const ICON_IDLE = 'text-muted-foreground hover:bg-card/70 hover:text-foreground'
const ROW = 'flex h-9 w-full items-center gap-2.5 rounded-md px-2.5 text-sm transition-colors'
/** Hàng của ngăn kéo điện thoại: cao hơn, chữ to hơn, bo tròn (vùng chạm ≥ 44px). */
const ROW_DRAWER = 'flex h-12 w-full items-center gap-3 rounded-xl px-3 text-[15px] transition-colors'
const ROW_ACTIVE = 'bg-accent font-medium text-primary'
const ROW_IDLE = 'text-foreground/85 hover:bg-card/70'

function IconButton({
  label,
  onClick,
  icon: Icon,
  small,
}: {
  label: string
  onClick: () => void
  icon: LucideIcon
  small?: boolean
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          onClick={onClick}
          className={cn(
            'flex shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-card/70 hover:text-foreground',
            small ? 'h-8 w-8' : 'h-10 w-10',
          )}
        >
          <Icon className={small ? 'h-4 w-4' : 'h-5 w-5'} />
        </button>
      </TooltipTrigger>
      <TooltipContent side={small ? 'bottom' : 'right'}>{label}</TooltipContent>
    </Tooltip>
  )
}

function RowAction({ label, icon: Icon, onClick }: { label: string; icon: LucideIcon; onClick: () => void }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          onClick={onClick}
          className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-card hover:text-primary"
        >
          <Icon className="h-4 w-4" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top">{label}</TooltipContent>
    </Tooltip>
  )
}

function EmptyTags({ drawer, canAdd }: { drawer?: boolean; canAdd: boolean }) {
  const { t } = useTranslation('convList')
  // Không có quyền tags.create → không gợi ý tạo (nút + đã ẩn).
  if (!canAdd) return <div className="px-2 py-2 text-xs text-muted-foreground">{t('rail.noTags')}</div>
  // Điện thoại không vào được Cài đặt (chỉ máy tính) → chỉ nút + ở dòng "Nhãn".
  return <div className="px-2 py-2 text-xs text-muted-foreground">{drawer ? t('rail.noTagsDrawer') : t('rail.noTagsDesktop')}</div>
}

// ─────────────────────────────────────────────────────────────────────────────
// Chọn tài khoản kênh
// ─────────────────────────────────────────────────────────────────────────────

function AccountPicker({
  accountId,
  onAccountChange,
  expanded,
  unreadByAccount,
}: {
  accountId?: string
  onAccountChange: (id?: string) => void
  expanded: boolean
  /** Số chưa đọc theo tài khoản — chỉ dùng để nhắc các tài khoản KHÁC tài khoản đang chọn (lib/inbox-account.ts). */
  unreadByAccount?: Record<string, number>
}) {
  const { t } = useTranslation('convList')
  const { data: accounts, isLoading } = useInboxAccounts()
  const othersUnread = othersUnreadTotal(unreadByAccount, accountId)
  const othersHint = othersUnread > 0 ? t('rail.othersUnread', { count: othersUnread }) : ''
  const list: ChannelAccount[] = accounts ?? []
  const selected = accountId ? list.find((a) => a.id === accountId) : undefined
  const selectedName = selected?.displayName ?? t('shared.unnamed')

  // Lọc theo nhóm kênh + ô tìm. Danh sách hiện ĐỦ mọi tài khoản khớp và cuộn
  // trong khung cao ~10 dòng — cắt còn 10 kèm "còn N nữa" thì tài khoản thứ 11
  // chỉ tìm ra được khi đã nhớ tên nó.
  const picker = useChannelPicker(list)
  const matched = picker.matched

  if (isLoading) {
    return (
      <div
        className={cn('animate-pulse rounded-lg border bg-muted', expanded ? 'h-10 w-full' : 'h-10 w-10')}
        aria-label={t('rail.loadingAccount')}
      />
    )
  }

  const face = selected ? (
    <span className="relative shrink-0">
      <Avatar className="h-8 w-8 rounded-md">
        <AvatarImage src={selected.avatarUrl ?? undefined} alt={selectedName} />
        <AvatarFallback className="rounded-md text-xs">{initials(selected.displayName)}</AvatarFallback>
      </Avatar>
      <PlatformBadge platform={selected.platform} />
    </span>
  ) : (
    <Users className="h-5 w-5 shrink-0 text-muted-foreground" />
  )

  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            {expanded ? (
              <button
                type="button"
                aria-label={t('rail.pickAccount')}
                className="flex h-11 w-full items-center gap-2 rounded-lg border bg-card px-2 text-left text-sm transition-colors hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center">{face}</span>
                <span className="min-w-0 flex-1 truncate">{selected ? selectedName : t('chSheet.allAccounts')}</span>
                {othersUnread > 0 && (
                  <span title={othersHint} aria-label={othersHint} className="flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-none text-destructive-foreground">
                    {shortUnreadCount(othersUnread)}
                  </span>
                )}
                <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
              </button>
            ) : (
              <button
                type="button"
                aria-label={othersHint ? t('rail.pickAccountHint', { hint: othersHint }) : t('rail.pickAccount')}
                className="relative flex h-10 w-10 items-center justify-center overflow-hidden rounded-lg border bg-card transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {face}
                {othersUnread > 0 && (
                  <span className="absolute right-0.5 top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[9px] font-bold leading-none text-destructive-foreground">
                    {shortUnreadCount(othersUnread)}
                  </span>
                )}
              </button>
            )}
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent side="right">{selected ? selectedName : t('chSheet.allAccounts')}{othersHint ? ` · ${othersHint}` : ''}</TooltipContent>
      </Tooltip>

      {/* Cao tối đa = phần màn hình còn trống (biến Radix cấp) — menu không
          bao giờ tràn khỏi viewport; phần đầu ghim, chỉ danh sách cuộn. */}
      <DropdownMenuContent
        align="start"
        side="right"
        collisionPadding={8}
        className="flex max-h-[var(--radix-dropdown-menu-content-available-height)] w-72 flex-col"
      >
        <DropdownMenuLabel>{t('chSheet.title')}</DropdownMenuLabel>
        <DropdownMenuSeparator />

        {/* Chỉ hiện ô tìm khi thật sự có nhiều tài khoản — vài tài khoản mà
            bắt gõ tìm thì thêm một bước thừa. */}
        {picker.showSearch && (
          <div className="px-2 pb-1.5 pt-1">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={picker.query}
                onChange={(e) => picker.setQuery(e.target.value)}
                placeholder={t('rail.searchAccounts')}
                className="h-8 pl-7 text-xs"
                // Menu tự chuyển tiêu điểm theo phím gõ; chặn để gõ được vào ô.
                onKeyDown={(e) => e.stopPropagation()}
              />
            </div>
          </div>
        )}

        {picker.showChips && (
          <ChannelChips
            groups={picker.groups}
            value={picker.channel}
            onChange={picker.setChannel}
            className="shrink-0 px-2 pb-1.5"
          />
        )}

        <DropdownMenuItem
          onSelect={() => onAccountChange(undefined)}
          className={cn('gap-2', !accountId && 'bg-accent text-accent-foreground')}
        >
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border">
            <Users className="h-4 w-4 text-muted-foreground" />
          </span>
          <span className="truncate">{t('chSheet.allAccounts')}</span>
        </DropdownMenuItem>

        {matched.length > 0 && <DropdownMenuSeparator />}

        {/* ~10 dòng (mỗi dòng ~44px) rồi cuộn; min-h-0 để khung co lại
            khi màn hình thấp hơn thế. */}
        <div className="max-h-[440px] min-h-0 overflow-y-auto">
          {matched.map((acc) => {
            const meta = statusMeta(acc.liveStatus, acc.isDisabled)
            const name = acc.displayName ?? t('shared.unnamed')
            const unreadHere = otherAccountUnread(unreadByAccount, accountId, acc.id)
            return (
              <DropdownMenuItem
                key={acc.id}
                onSelect={() => onAccountChange(acc.id)}
                className={cn('gap-2', accountId === acc.id && 'bg-accent text-accent-foreground')}
              >
                <span className="relative shrink-0">
                  <Avatar className="h-7 w-7">
                    <AvatarImage src={acc.avatarUrl ?? undefined} alt={name} />
                    <AvatarFallback className="text-xs">{initials(acc.displayName)}</AvatarFallback>
                  </Avatar>
                  <PlatformBadge platform={acc.platform} />
                </span>
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="truncate">{name}</span>
                  <PlatformTag platform={acc.platform} className="self-start" />
                </span>
                {unreadHere > 0 && (
                  <span title={t('shared.unreadCount', { count: unreadHere })} className="flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-none text-destructive-foreground">
                    {shortUnreadCount(unreadHere)}
                  </span>
                )}
                {isAccountDown(acc) ? (
                  // Rớt kết nối (đỏ) / đang kết nối lại (vàng): nhãn + nút dựng lại
                  // ngay tại chỗ, không bắt người dùng đi tìm trang Tích hợp.
                  <span className="flex shrink-0 flex-col items-end gap-1">
                    <Badge variant={meta.variant} className="px-1.5 py-0 text-[10px]">
                      {meta.label}
                    </Badge>
                    <ReconnectButton account={acc} />
                  </span>
                ) : (
                  <Badge variant={meta.variant} className="shrink-0 px-1.5 py-0 text-[10px]">
                    {meta.label}
                  </Badge>
                )}
              </DropdownMenuItem>
            )
          })}
        </div>

        {list.length > 0 && matched.length === 0 && (
          <div className="px-2 py-3 text-center text-xs text-muted-foreground">
            {t('rail.noMatchAccounts')}
          </div>
        )}

        {list.length === 0 && (
          <div className="px-2 py-3 text-center text-xs text-muted-foreground">
            {t('rail.noAccounts')}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
